import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, FileText, Trash2, Eye, Copy, Search } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

const quoteTotal = (q) => {
  const sub = (q.items || []).reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0);
  return sub + sub * ((q.tax_rate || 0) / 100);
};

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    api.get("/quotes").then((r) => setQuotes(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const askRemove = (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteId(id);
  };

  const confirmRemove = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/quotes/${deleteId}`);
      setQuotes((q) => q.filter((x) => x.id !== deleteId));
      toast.success("Preventivo eliminato");
    } catch {
      toast.error("Errore durante l'eliminazione");
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const [duplicatingId, setDuplicatingId] = useState(null);

  const duplicate = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDuplicatingId(id);
    try {
      const { data } = await api.post(`/quotes/${id}/duplicate`);
      setQuotes((q) => [data, ...q]);
      toast.success("Preventivo duplicato");
    } catch {
      toast.error("Errore durante la duplicazione");
    } finally {
      setDuplicatingId(null);
    }
  };

  const statusBadge = {
    draft: "bg-slate-100 text-slate-600",
    sent: "bg-amber-100 text-amber-700",
    signed: "bg-emerald-100 text-emerald-700",
  };
  const statusLabel = { draft: "Bozza", sent: "Inviato", signed: "Firmato" };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return quotes.filter((x) => {
      if (statusFilter !== "all" && x.status !== statusFilter) return false;
      if (!q) return true;
      return [x.title, x.number, x.client_name].some((f) => (f || "").toLowerCase().includes(q));
    });
  }, [quotes, search, statusFilter]);

  const statusFilters = [
    { key: "all", label: "Tutti" },
    { key: "draft", label: "Bozze" },
    { key: "sent", label: "Inviati" },
    { key: "signed", label: "Firmati" },
  ];

  return (
    <Layout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">Preventivi</h1>
          <p className="text-slate-500 mt-1">Gestisci tutte le tue offerte.</p>
        </div>
        <Link to="/quotes/new">
          <Button data-testid="quotes-new-button" className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" /> Nuovo Preventivo
          </Button>
        </Link>
      </div>

      {loading ? (
        <p className="text-slate-500">Caricamento…</p>
      ) : quotes.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-16 text-center">
          <FileText className="w-10 h-10 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-900 font-semibold">Nessun preventivo</p>
          <p className="text-slate-500 text-sm mt-1 mb-6">Crea il tuo primo preventivo con descrizioni AI.</p>
          <Link to="/quotes/new">
            <Button className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-2" /> Crea ora</Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="quotes-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca per titolo, numero o cliente…" className="pl-9" />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {statusFilters.map((s) => (
                <button key={s.key} onClick={() => setStatusFilter(s.key)}
                  data-testid={`quotes-filter-${s.key}`}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                    statusFilter === s.key
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-3">Numero</th>
                <th className="text-left px-4 py-3">Titolo</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Cliente</th>
                <th className="text-left px-4 py-3">Stato</th>
                <th className="text-right px-4 py-3">Totale</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500" data-testid="quotes-no-results">
                  Nessun preventivo corrisponde alla ricerca.
                </td></tr>
              ) : filtered.map((q) => (
                <tr key={q.id} className="hover:bg-slate-50 transition-colors" data-testid={`quote-row-${q.id}`}>
                  <td className="px-4 py-3 text-sm font-mono text-slate-500">{q.number}</td>
                  <td className="px-4 py-3">
                    <Link to={`/quotes/${q.id}/edit`} className="font-semibold text-slate-900 hover:text-blue-600">
                      {q.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 hidden md:table-cell">{q.client_name || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusBadge[q.status]}`}>
                      {statusLabel[q.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmt(quoteTotal(q))}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/quotes/${q.id}`} data-testid={`view-quote-${q.id}`}
                        className="p-2 text-slate-400 hover:text-blue-600 rounded-md hover:bg-slate-100">
                        <Eye className="w-4 h-4" />
                      </Link>
                      <button onClick={(e) => duplicate(q.id, e)} disabled={duplicatingId === q.id}
                        data-testid={`duplicate-quote-${q.id}`} title="Duplica"
                        className="p-2 text-slate-400 hover:text-blue-600 rounded-md hover:bg-slate-100 disabled:opacity-50">
                        <Copy className="w-4 h-4" />
                      </button>
                      <button onClick={(e) => askRemove(q.id, e)} data-testid={`delete-quote-${q.id}`}
                        className="p-2 text-slate-400 hover:text-red-600 rounded-md hover:bg-slate-100">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-quote-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo preventivo?</AlertDialogTitle>
            <AlertDialogDescription>
              L'operazione è irreversibile. Il preventivo verrà rimosso definitivamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-quote-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove} disabled={deleting}
              data-testid="delete-quote-confirm"
              className="bg-red-600 hover:bg-red-700">
              {deleting ? "Eliminazione…" : "Elimina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
